"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import Footer from "@/components/marketing/footer";

export default function WhyContractLucidityPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const show = (id: string) => visibleSections.has(id);

  const comparisonRows = [
    { capability: "Clause-by-clause severity (R/Y/G)", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "Version-aware AI pipeline", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "Full-document text diff redline", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "Obligation calendar integration (ICS)", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "Cross-document intelligence", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "AI-assisted clause revision", cl: true, harvey: true, spellbook: true, ironclad: false },
    { capability: "DOCX with redlined revisions", cl: true, harvey: false, spellbook: true, ironclad: false },
    { capability: "Self-hosted deployment", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "Multi-AI provider", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "OIDC Single Sign-On", cl: true, harvey: true, spellbook: false, ironclad: true },
    { capability: "SCIM 2.0 provisioning", cl: true, harvey: false, spellbook: false, ironclad: true },
    { capability: "Role-based access control", cl: true, harvey: true, spellbook: false, ironclad: true },
    { capability: "Ethical walls / information barriers", cl: true, harvey: false, spellbook: false, ironclad: false },
    { capability: "REST API with OAuth 2.0", cl: true, harvey: false, spellbook: false, ironclad: false },
  ];

  const faqs = [
    {
      q: "Is this a CLM?",
      a: "No \u2014 and that\u2019s intentional. CLMs (Ironclad, Icertis, Agiloft) manage contracts your team creates: templates, drafting workflows, approval routing, e-signature, and repository storage. Contract Lucidity handles the opposite problem: when a contract arrives from outside \u2014 opposing counsel\u2019s paper, a vendor\u2019s MSA, a partner\u2019s NDA \u2014 and needs to be read, analyzed, and responded to. Your CLM can\u2019t tell you the indemnification clause is one-sided. We can. They\u2019re complementary, not competitive.",
    },
    {
      q: "Do I need to work in Microsoft Word?",
      a: "No. Contract Lucidity is a web application. You upload documents, review in the browser, and download a Word document showing original text in strikethrough and revised language underlined when you\u2019re ready to send it.",
    },
    {
      q: "What about data security?",
      a: "Self-hosted Docker deployment. Your data stays on your infrastructure. Choose your AI provider. OIDC Single Sign-On and SCIM 2.0 provisioning integrate with your existing identity provider. Role-based access control, group-based project access, and ethical walls for matter-level information barriers (ABA Model Rule 1.10). No vendor lock-in.",
    },
    {
      q: "How does pricing compare?",
      a: "Harvey AI starts at $288K/year minimum. Contract Lucidity starts at $2,500/month. Full lifecycle capabilities at a fraction of the cost.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", scrollBehavior: "smooth" }}>
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {/* --- NAV --- */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 40 ? "bg-white shadow-md py-1" : "bg-white/90 py-1.5"
      }`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <Link href="/" className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Contract Lucidity" className="h-12 md:h-14" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link href="/features" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Features</Link>
            <Link href="/how-it-works" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>How It Works</Link>
            <Link href="/law-firms" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Law Firms</Link>
            <Link href="/in-house" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>In-House</Link>
            <Link href="/security" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Security</Link>
            <Link href="/why-contract-lucidity" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Why Us</Link>
            <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#C8A84B] hover:text-[#b8993f] transition-colors font-semibold" style={{ fontFamily: "system-ui, sans-serif" }}>Lucid Vault</a>
            <Link
              href="/login"
              className="px-6 py-2.5 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-[#2E5EA0] transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Sign In
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" aria-label="Toggle menu">
            <svg className="w-6 h-6 text-[#1B3A6B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" style={{ fontFamily: "system-ui, sans-serif" }}>
            <div className="flex flex-col px-6 py-4 gap-3">
              <Link href="/features" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Features</Link>
              <Link href="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">How It Works</Link>
              <Link href="/law-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Law Firms</Link>
              <Link href="/in-house" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">In-House</Link>
              <Link href="/security" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Security</Link>
              <Link href="/why-contract-lucidity" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Why Us</Link>
              <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="text-sm text-[#C8A84B] font-semibold hover:text-[#b8993f] py-2 border-b border-gray-50">Lucid Vault</a>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="text-sm font-semibold text-white bg-[#1B3A6B] rounded px-4 py-2.5 text-center mt-2">Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* --- HERO --- */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-6" style={{ fontFamily: "system-ui, sans-serif" }}>
            Why Contract Lucidity
          </p>
          <h1 className="text-4xl md:text-5xl leading-[1.15] mb-6 text-[#1B3A6B]">
            What every other contract review tool gets wrong.
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
            Most tools analyze a contract once and call it done. Contracts aren&apos;t static &mdash; they go through 5 to 15 revision cycles before signature. Your tool should understand that.
          </p>
        </div>
      </section>

      {/* --- SECTION 1: THE VERSION PROBLEM --- */}
      <section id="version-problem" data-animate className="py-24 bg-[#1B3A6B]">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("version-problem") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-3xl md:text-4xl text-white leading-snug mb-8">
              Every other tool treats each upload as a blank slate.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-8" style={{ fontFamily: "system-ui, sans-serif" }}>
              Upload a revised contract to any competitor &mdash; Harvey, Spellbook, LegalFly, Definely &mdash; and you get a completely new analysis. Different clause categories. Different severity levels. Different obligation lists. Even if the document only changed by 2%. Your attorney&apos;s prior decisions, your firm&apos;s review work, your calendar events &mdash; all gone. Start over.
            </p>
            <div className="inline-block h-px w-16 bg-[#C8A84B] mb-8" />
            <p className="text-2xl text-[#C8A84B] leading-snug">
              Contract Lucidity doesn&apos;t do that.
            </p>
          </div>
        </div>
      </section>

      {/* --- SECTION 2: THREE DIFFERENTIATORS --- */}
      <section id="differentiators" data-animate className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Version-Aware AI Pipeline",
                bullets: [
                  "When you upload a revised contract, our system runs a programmatic text diff first \u2014 no AI involved.",
                  "For each existing clause, it asks one question: \u201CDid the revision change anything relevant to this clause?\u201D",
                  "If no \u2192 the analysis carries forward exactly. Zero AI involvement. Zero drift. Zero cost.",
                  "If yes \u2192 focused re-evaluation of just that clause, with the specific changes as context.",
                  "Your executive summary, negotiation strategy, obligations \u2014 all preserved unless the change genuinely warrants an update.",
                ],
                footer: "No other tool in the market does this.",
              },
              {
                title: "Full Document Lifecycle",
                intro: "Most tools cover one part of the workflow. Spellbook does redlining in Word. Kira does extraction for due diligence. Ironclad manages templates and workflows.",
                pipeline: "Upload \u2192 Classify \u2192 Analyze (clause-by-clause with severity) \u2192 Review (AI-assisted revision) \u2192 Generate DOCX with redlined revisions \u2192 Track Obligations \u2192 Upload Revision \u2192 Redline Comparison \u2192 Intelligent Carry-Forward",
                footer: "One platform. No integrations required.",
              },
              {
                title: "Obligation Management That Actually Works",
                bullets: [
                  "Every contract creates obligations. Payment deadlines, renewal windows, compliance requirements.",
                  "We extract them automatically, let you add custom ones, auto-calculate due dates from priority.",
                  "Export directly to Outlook or Google Calendar \u2014 proper RFC 5545 events with recurring reminders, full metadata, and a clickable link back to the obligation.",
                  "Upload a revised version? Your calendar events carry forward.",
                ],
                footer: "No other platform connects contract analysis to your calendar.",
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`bg-[#F8F9FB] rounded-lg p-8 border border-gray-100 transition-all duration-700 ${
                  show("differentiators") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <h3 className="text-xl text-[#1B3A6B] mb-5">{card.title}</h3>
                {"intro" in card && (
                  <p className="text-sm text-gray-500 leading-relaxed mb-4" style={{ fontFamily: "system-ui, sans-serif" }}>
                    {card.intro}
                  </p>
                )}
                {"pipeline" in card && (
                  <p className="text-sm text-[#1B3A6B] font-semibold leading-relaxed mb-4" style={{ fontFamily: "system-ui, sans-serif" }}>
                    {card.pipeline}
                  </p>
                )}
                {"bullets" in card && (
                  <ul className="space-y-3 mb-4">
                    {card.bullets!.map((b, j) => (
                      <li key={j} className="text-sm text-gray-500 leading-relaxed flex gap-2" style={{ fontFamily: "system-ui, sans-serif" }}>
                        <span className="text-[#C8A84B] shrink-0 mt-0.5">&bull;</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="inline-block h-px w-12 bg-[#C8A84B] mb-4" />
                <p className="text-sm font-semibold text-[#1B3A6B]" style={{ fontFamily: "system-ui, sans-serif" }}>
                  {card.footer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- SECTION 3: COMPETITIVE COMPARISON TABLE --- */}
      <section id="comparison" data-animate className="py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-700 ${show("comparison") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Competitive Comparison</p>
            <h2 className="text-3xl md:text-4xl text-[#1B3A6B]">How we stack up.</h2>
          </div>
          <div className={`overflow-x-auto transition-all duration-700 ${show("comparison") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: "200ms" }}>
            <table className="w-full text-left border-collapse" style={{ fontFamily: "system-ui, sans-serif" }}>
              <thead>
                <tr className="border-b-2 border-[#1B3A6B]">
                  <th className="py-4 pr-6 text-sm font-semibold text-[#1B3A6B]">Capability</th>
                  <th className="py-4 px-4 text-sm font-semibold text-[#1B3A6B] text-center">Contract Lucidity</th>
                  <th className="py-4 px-4 text-sm font-semibold text-gray-400 text-center">Harvey AI</th>
                  <th className="py-4 px-4 text-sm font-semibold text-gray-400 text-center">Spellbook</th>
                  <th className="py-4 px-4 text-sm font-semibold text-gray-400 text-center">Ironclad</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-200">
                    <td className="py-4 pr-6 text-sm text-gray-700">{row.capability}</td>
                    <td className="py-4 px-4 text-center">
                      {row.cl ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </span>
                      ) : (
                        <span className="inline-block w-4 h-0.5 bg-gray-300 rounded" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.harvey ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </span>
                      ) : (
                        <span className="inline-block w-4 h-0.5 bg-gray-300 rounded" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.spellbook ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </span>
                      ) : (
                        <span className="inline-block w-4 h-0.5 bg-gray-300 rounded" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.ironclad ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </span>
                      ) : (
                        <span className="inline-block w-4 h-0.5 bg-gray-300 rounded" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* --- SECTION 4: FAQ --- */}
      <section id="faq" data-animate className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-700 ${show("faq") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>Common Questions</p>
            <h2 className="text-3xl md:text-4xl text-[#1B3A6B]">But what about...</h2>
          </div>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`border-b border-gray-100 pb-8 transition-all duration-700 ${
                  show("faq") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <h3 className="text-lg text-[#1B3A6B] mb-3">&ldquo;{faq.q}&rdquo;</h3>
                <p className="text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- SECTION 5: CLM Positioning --- */}
      <section id="clm" data-animate className="py-24 bg-[#1B3A6B]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("clm") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-16">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>&ldquo;We Already Have a CLM&rdquo;</p>
              <h2 className="text-3xl md:text-4xl text-white leading-snug">
                Great. Who reads the contracts<br />
                <span className="text-[#C8A84B]">that land on your desk from outside?</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Left: Their Paper */}
              <div className="bg-white/10 backdrop-blur rounded-xl p-8 border border-white/20">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-10 h-10 rounded-full bg-[#C8A84B] text-[#1B3A6B] flex items-center justify-center text-sm font-bold" style={{ fontFamily: "system-ui, sans-serif" }}>CL</span>
                  <div>
                    <h3 className="text-lg text-white font-semibold">Their Paper Arrives</h3>
                    <p className="text-white/50 text-xs" style={{ fontFamily: "system-ui, sans-serif" }}>Contract Lucidity</p>
                  </div>
                </div>
                <div className="space-y-3" style={{ fontFamily: "system-ui, sans-serif" }}>
                  {[
                    "Opposing counsel sends their MSA",
                    "Upload → AI analyzes every clause",
                    "Attorney reviews findings with AI assistance",
                    "Generate redlined revision document",
                    "Track obligations with calendar integration",
                    "Upload their counter → intelligent carry-forward",
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-[#C8A84B] text-xs font-bold mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-white/80 text-sm">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Your Paper */}
              <div className="bg-white/5 rounded-xl p-8 border border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center text-sm font-bold" style={{ fontFamily: "system-ui, sans-serif" }}>CLM</span>
                  <div>
                    <h3 className="text-lg text-white/70 font-semibold">Your Paper Goes Out</h3>
                    <p className="text-white/30 text-xs" style={{ fontFamily: "system-ui, sans-serif" }}>Ironclad, Icertis, Agiloft, etc.</p>
                  </div>
                </div>
                <div className="space-y-3" style={{ fontFamily: "system-ui, sans-serif" }}>
                  {[
                    "Start from your approved template",
                    "Fill in deal-specific terms",
                    "Route for internal approval",
                    "Send for e-signature",
                    "Store executed contract",
                    "Set renewal reminders",
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-white/30 text-xs font-bold mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-white/50 text-sm">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CLM Comparison Graphic */}
            <div className="mb-12 rounded-xl overflow-hidden border border-white/20 shadow-lg">
              <img src="/images/clm-comparison.webp" alt="Contract Lucidity vs CLM — complementary workflows for inbound and outbound contracts" className="w-full" loading="lazy" />
            </div>

            <div className="text-center">
              <div className="inline-block px-6 py-3 bg-[#C8A84B]/20 border border-[#C8A84B]/40 rounded-xl">
                <p className="text-white text-sm" style={{ fontFamily: "system-ui, sans-serif" }}>
                  <span className="font-bold text-[#C8A84B]">Complementary, not competitive.</span>
                  {" "}Use your CLM for outbound contracts. Use Contract Lucidity for inbound third-party paper.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- CTA --- */}
      <section id="cta" data-animate className="py-24 bg-[#C8A84B]">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("cta") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 text-center shadow-lg">
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
                See the difference for yourself.
              </h2>
              <p className="text-gray-500 mb-10 max-w-xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Upload a real contract. Watch the version-aware pipeline in action. See what no other tool can do.
              </p>
              <a
                href="/demo"
                className="inline-block px-10 py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-white hover:text-[#1B3A6B] hover:ring-2 hover:ring-[#1B3A6B] transition-all"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
